import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import type { QueryClient } from '@tanstack/react-query';
import { queryClient } from '@/lib/query-client';
import { clearMFAEnrollmentAccount } from '@/lib/mfa-enrollment-account';
import { getApiCacheScope } from '@/lib/offline/service-worker-cache';

/**
 * Configured API base URL: VITE_API_BASE_URL_OVERRIDE when set and non-empty,
 * otherwise VITE_API_BASE_URL. Lets a staging deployment point at a different
 * backend without changing the shared VITE_API_BASE_URL.
 */
export const getApiBaseUrlEnv = (): string | undefined =>
  import.meta.env.VITE_API_BASE_URL_OVERRIDE || import.meta.env.VITE_API_BASE_URL;

const getApiBaseUrl = (): string => {
  const envBaseUrl = getApiBaseUrlEnv();
  if (envBaseUrl) {
    return envBaseUrl.replace(/\/+$/, '');
  }
  if (import.meta.env.PROD) {
    return 'https://api.h3ight.com';
  }
  return 'http://localhost:8000';
};

export const API_BASE_URL = getApiBaseUrl();

/**
 * Resolve a file/media URL returned by the API against the API origin.
 * Serializers without request context emit host-relative paths such as
 * `/media/...`; used as-is in an `<a href>` those resolve against the SPA
 * host instead of the API host. Absolute URLs are returned unchanged.
 */
export function resolveMediaUrl(url: string): string;
export function resolveMediaUrl(url: string | null | undefined): string | undefined;
export function resolveMediaUrl(url: string | null | undefined): string | undefined {
  if (!url) return undefined;
  if (/^[a-z][a-z0-9+.-]*:/i.test(url) || url.startsWith('//')) return url;
  if (!API_BASE_URL) return url;
  return `${API_BASE_URL}${url.startsWith('/') ? '' : '/'}${url}`;
}

export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // send httpOnly auth cookies with every request
});

// ---------------------------------------------------------------------------
// Auth state helpers — tokens live in httpOnly cookies (not accessible to JS).
// A lightweight non-httpOnly flag cookie lets JS check login state.
// ---------------------------------------------------------------------------

const AUTH_FLAG_COOKIE = 'h3gear_authenticated';

// In-memory CSRF token; see "CSRF double-submit token" below.
let _csrfToken: string | null = null;
let csrfGeneration = 0;

type ApiRequestConfig = InternalAxiosRequestConfig & {
  _retry?: boolean;
  _csrfRetry?: boolean;
  _rateLimitRetry?: boolean;
  _csrfGeneration?: number;
  _csrfTokenOverride?: string;
};

/**
 * Set the auth-flag cookie client-side so JS can check login state.
 * In cross-origin deployments the server-set flag cookie is scoped to
 * the API hostname and invisible to the frontend origin, so we always
 * set it on the frontend origin as well.
 */
export const setAuthFlag = (): void => {
  // 7 days — matches the default SIMPLE_JWT REFRESH_TOKEN_LIFETIME
  document.cookie = `${AUTH_FLAG_COOKIE}=true; path=/; max-age=604800`;
};

/**
 * Returns true when the auth-flag cookie is present.
 * This does NOT validate the token — it only tells us if the browser
 * currently has a session cookie set by the backend (or client-side
 * after login).
 */
export const isAuthenticated = (): boolean => {
  return document.cookie.split('; ').some((c) => c.startsWith(`${AUTH_FLAG_COOKIE}=`));
};

/** @deprecated Tokens are now in httpOnly cookies. Kept temporarily for migration. */
export const getAccessToken = (): string | null => null;
/** @deprecated Tokens are now in httpOnly cookies. */
export const getRefreshToken = (): string | null => null;
/** @deprecated Tokens are now in httpOnly cookies. */
export const setTokens = (_accessToken: string, _refreshToken: string): void => { /* noop */ };
/** @deprecated Tokens are now in httpOnly cookies. */
export const clearTokens = (client: QueryClient = queryClient): void => {
  // Clear the JS-readable flag cookie
  document.cookie = `${AUTH_FLAG_COOKIE}=; Max-Age=0; path=/`;
  _csrfToken = null;
  clearMFAEnrollmentAccount();
  // Drop all cached organization-scoped data so the next tenant cannot see
  // the previous tenant's React Query cache after logout / session expiry.
  // The caller (AuthContext) can pass the active QueryClient so tests that
  // provide their own client clear the right instance.
  client.clear();
};

// ---------------------------------------------------------------------------
// Portal access token — sent as X-Portal-Token header
// ---------------------------------------------------------------------------

let activePortalToken: string | null = null;

export const setPortalToken = (token: string | null): void => {
  activePortalToken = token;
};

export const getPortalToken = (): string | null => activePortalToken;

// ---------------------------------------------------------------------------
// Token refresh queue — silent refresh via httpOnly cookie
// ---------------------------------------------------------------------------

let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value: unknown) => void;
  reject: (reason?: unknown) => void;
}> = [];

const processQueue = (error: Error | null): void => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(undefined);
    }
  });
  failedQueue = [];
};

// ---------------------------------------------------------------------------
// Error classification helpers
// ---------------------------------------------------------------------------

// Maximum time a 429 retry will block the UI before retrying once.
export const MAX_RETRY_AFTER_MS = 30_000;

function isAuthFailure(status: number | undefined): boolean {
  // 401/403 from an auth/refresh endpoint means the session is no longer
  // valid.  Any other status (including 429 and 5xx) must not be treated as a
  // logout trigger.
  return status === 401 || status === 403;
}

// Exposed for tests so the parsing logic can be exercised directly.
export function parseRetryAfterMs(error: AxiosError): number {
  const headers = error.response?.headers;
  const value = headers?.['retry-after'] ?? headers?.['Retry-After'];

  if (typeof value === 'string' || typeof value === 'number') {
    const seconds = Number(value);
    // Retry-After is defined as a non-negative integer; 0 means "retry immediately".
    // We keep this explicit so it does not fall through to the HTTP-date fallback.
    if (!Number.isNaN(seconds) && Number.isFinite(seconds) && seconds >= 0) {
      return seconds * 1000;
    }
    // Retry-After may be an HTTP-date.
    const date = new Date(String(value));
    if (!Number.isNaN(date.getTime())) {
      return Math.max(0, date.getTime() - Date.now());
    }
  }

  // Default backoff when the server doesn't tell us how long to wait.
  return 1000;
}

// Caps the parsed Retry-After delay so a large header value cannot freeze the UI.
export function getRetryAfterDelayMs(error: AxiosError, maxMs = MAX_RETRY_AFTER_MS): number {
  return Math.min(parseRetryAfterMs(error), maxMs);
}

// ---------------------------------------------------------------------------
// CSRF double-submit token
// ---------------------------------------------------------------------------
// The backend sets a non-httpOnly `csrftoken` cookie on the API origin and
// also exposes the same value in the X-CSRFToken response header.  When the
// SPA is served from a different origin than the API the cookie is invisible
// to `document.cookie`, so the response value is kept in memory as a fallback.

function getCsrfCookie(): string | null {
  const match = document.cookie.split('; ').find((c) => c.startsWith('csrftoken='));
  return match ? match.split('=')[1] : null;
}

export function getCsrfToken(): string | null {
  return getCsrfCookie() || _csrfToken;
}

export function setCsrfToken(token: string | null): void {
  if (!Object.is(_csrfToken, token)) {
    csrfGeneration += 1;
  }
  _csrfToken = token;
}

function getCsrfResponseHeader(headers: unknown): string | null {
  if (!headers || typeof headers !== 'object') return null;

  const getter = (headers as { get?: unknown }).get;
  if (typeof getter === 'function') {
    const token = getter.call(headers, 'x-csrftoken');
    return typeof token === 'string' && token ? token : null;
  }

  const values = headers as Record<string, unknown>;
  const token = values['x-csrftoken'] ?? values['X-CSRFToken'];
  return typeof token === 'string' && token ? token : null;
}

function getCsrfBodyToken(data: unknown): string | null {
  const bodyToken = (data as Record<string, unknown> | null | undefined)?.csrftoken;
  return typeof bodyToken === 'string' && bodyToken ? bodyToken : null;
}

export function captureCsrfToken(data: unknown, headers?: unknown): string | null {
  const bodyToken = getCsrfBodyToken(data);
  const token = getCsrfResponseHeader(headers)
    ?? bodyToken;
  if (typeof token === 'string' && token) {
    setCsrfToken(token);
    return token;
  }
  return null;
}

function captureCsrfTokenForRequest(
  data: unknown,
  headers: unknown,
  config: ApiRequestConfig | undefined,
): string | null {
  if (
    !getCsrfBodyToken(data)
    &&
    config?._csrfGeneration !== undefined
    && config._csrfGeneration < csrfGeneration
  ) {
    return null;
  }
  return captureCsrfToken(data, headers);
}

function invalidateCsrfToken(): void {
  _csrfToken = null;
  csrfGeneration += 1;
}

let csrfBootstrapPromise: Promise<string | null> | null = null;

async function bootstrapCsrfToken(): Promise<string | null> {
  if (!csrfBootstrapPromise) {
    csrfBootstrapPromise = axios.get(
      `${API_BASE_URL}/api/v1/auth/csrf/`,
      { withCredentials: true },
    )
      .then((response) => captureCsrfToken(response.data, response.headers))
      .catch(() => null)
      .finally(() => {
        csrfBootstrapPromise = null;
      });
  }

  return csrfBootstrapPromise;
}

export async function ensureCsrfToken(): Promise<string | null> {
  return getCsrfToken() ?? bootstrapCsrfToken();
}

function isCsrfError(data: unknown): boolean {
  const values = data as Record<string, unknown> | null | undefined;
  const message = typeof values?.detail === 'string'
    ? values.detail
    : typeof values?.error === 'string'
      ? values.error
      : '';
  return message.toLowerCase().includes('csrf');
}

export async function refreshSession(): Promise<void> {
  const token = await ensureCsrfToken();
  if (!token) {
    throw new Error('Unable to establish a CSRF token.');
  }
  const sendRefresh = (csrfToken: string) => axios.post(
    `${API_BASE_URL}/api/v1/auth/refresh/`,
    {},
    {
      withCredentials: true,
      headers: { 'X-CSRFToken': csrfToken },
    },
  );

  let response: Awaited<ReturnType<typeof sendRefresh>>;
  try {
    response = await sendRefresh(token);
  } catch (error) {
    if (
      !axios.isAxiosError(error)
      || error.response?.status !== 403
      || !isCsrfError(error.response.data)
    ) {
      throw error;
    }

    invalidateCsrfToken();
    const freshToken = await bootstrapCsrfToken();
    if (!freshToken) throw error;
    response = await sendRefresh(freshToken);
  }
  captureCsrfToken(response.data, response.headers);
  setAuthFlag();
}

function isOfflineQueueEligibleRequest(config: InternalAxiosRequestConfig): boolean {
  const method = (config.method ?? '').toUpperCase();
  if (!['POST', 'PUT', 'PATCH'].includes(method) || !config.url) return false;

  const pathname = new URL(config.url, API_BASE_URL).pathname;
  return pathname.startsWith('/api/')
    && !pathname.startsWith('/api/v1/auth/')
    && !pathname.includes('/kiosk-sign/');
}

// Attach X-CSRFToken header to every mutating request and X-Portal-Token
// to portal endpoints.
apiClient.interceptors.request.use(async (config) => {
  const requestConfig = config as ApiRequestConfig;
  const method = (config.method ?? '').toUpperCase();
  if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
    config.headers.delete('Content-Type');
  }
  if (
    typeof navigator !== 'undefined' &&
    'serviceWorker' in navigator &&
    method === 'GET' &&
    config.url?.includes('/api/')
  ) {
    config.params = {
      ...(config.params ?? {}),
      _cache_scope: getApiCacheScope(),
    };
  }
  if (['POST', 'PUT', 'PATCH', 'DELETE'].includes(method)) {
    const tokenOverride = requestConfig._csrfTokenOverride;
    delete requestConfig._csrfTokenOverride;
    const token = tokenOverride ?? getCsrfToken() ?? await ensureCsrfToken();
    if (!token) {
      if (!isOfflineQueueEligibleRequest(config)) {
        throw new Error('Unable to establish a CSRF token.');
      }
    } else {
      config.headers.set('X-CSRFToken', token);
    }
  }
  if (activePortalToken && config.url?.includes('/api/v1/projects/portal')) {
    config.headers.set('X-Portal-Token', activePortalToken);
  }
  requestConfig._csrfGeneration = csrfGeneration;
  return config;
});

apiClient.interceptors.response.use(
  (response) => {
    captureCsrfTokenForRequest(
      response.data,
      response.headers,
      response.config as ApiRequestConfig,
    );
    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as ApiRequestConfig | undefined;
    captureCsrfTokenForRequest(
      error.response?.data,
      error.response?.headers,
      originalRequest,
    );

    if (!originalRequest) {
      return Promise.reject(error);
    }

    if (error.response?.status === 401 && !originalRequest._retry) {
      // If the user was never authenticated (no flag cookie), there is no
      // refresh token to try.  Propagate the original error so callers
      // (e.g. login form) can display it to the user.
      if (!isAuthenticated()) {
        return Promise.reject(error);
      }

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then(() => apiClient(originalRequest))
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        await refreshSession();
        processQueue(null);
        return apiClient(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError as Error);

        // Only log the user out when the refresh endpoint itself says the
        // session is invalid.  Network errors and 5xx/429 responses from the
        // refresh endpoint should keep the session intact and propagate the
        // original failure instead of forcing a logout.
        const refreshAxiosError = axios.isAxiosError(refreshError)
          ? (refreshError as AxiosError)
          : null;
        if (isAuthFailure(refreshAxiosError?.response?.status)) {
          clearTokens();
          window.location.href = '/login';
        }
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    // -----------------------------------------------------------------
    // CSRF 403 recovery — if the backend rejects a mutating request
    // because the X-CSRFToken header is missing or stale, force a
    // token capture/bootstrap and retry the original request once.
    // -----------------------------------------------------------------
    if (error.response?.status === 403 && !originalRequest._csrfRetry) {
      if (isCsrfError(error.response.data)) {
        originalRequest._csrfRetry = true;
        invalidateCsrfToken();
        const token = await bootstrapCsrfToken();
        if (!token) return Promise.reject(error);
        originalRequest._csrfTokenOverride = token;
        return apiClient(originalRequest);
      }
    }

    // Forced account setup gates.
    if (error.response?.status === 403) {
      const data = error.response.data as Record<string, unknown> | undefined;
      if (data?.code === 'membership_inactive') {
        window.dispatchEvent(new CustomEvent('membership-inactive', {
          detail: {
            organizationId: data.organization_id,
            organizationName: data.organization_name,
          },
        }));
        return Promise.reject(error);
      }
      if (data?.code === 'password_change_required' && window.location.pathname !== '/change-password') {
        window.location.href = '/change-password';
        return Promise.reject(error);
      }
      if (data?.code === 'mfa_enrollment_required' && window.location.pathname !== '/enroll-mfa') {
        window.location.href = '/enroll-mfa';
        return Promise.reject(error);
      }
      if (data?.code === 'terms_acceptance_required') {
        window.dispatchEvent(new CustomEvent('terms-acceptance-required'));
        return Promise.reject(error);
      }
    }

    // -----------------------------------------------------------------
    // 429 Too Many Requests — respect Retry-After and retry once instead
    // of treating the rate-limit like an auth failure.  This keeps the
    // user on the current page and never clears their session.
    // -----------------------------------------------------------------
    if (error.response?.status === 429 && !originalRequest._rateLimitRetry) {
      originalRequest._rateLimitRetry = true;
      const delayMs = getRetryAfterDelayMs(error);
      await new Promise((resolve) => { setTimeout(resolve, delayMs); });
      return apiClient(originalRequest);
    }

    return Promise.reject(error);
  }
);

export interface ApiError {
  message: string;
  status: number;
  code?: string;
  errors?: Record<string, string[]>;
}

export const handleApiError = (error: unknown): ApiError => {
  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError<{ error?: string; detail?: string; [key: string]: unknown }>;
    const status = axiosError.response?.status || 500;
    const data = axiosError.response?.data;

    if (status === 429) {
      const retryAfterHeader =
        axiosError.response?.headers?.['retry-after'] ??
        axiosError.response?.headers?.['Retry-After'];
      const hasRetryAfter =
        retryAfterHeader != null && String(retryAfterHeader).trim() !== '';
      const retryAfterText = hasRetryAfter
        ? (() => {
            const retryAfterMs = getRetryAfterDelayMs(axiosError);
            const retryAfterSeconds = Math.max(0, Math.ceil(retryAfterMs / 1000));
            return retryAfterSeconds > 0 ? ` (retry after ${retryAfterSeconds}s)` : '';
          })()
        : '';
      const message = data?.error || data?.detail || `Too many requests${retryAfterText}. Please slow down.`;
      return { message, status, code: typeof data?.code === 'string' ? data.code : undefined };
    }

    if (data?.error) {
      return { message: data.error, status, code: typeof data.code === 'string' ? data.code : undefined };
    }

    if (data?.detail) {
      return { message: data.detail, status, code: typeof data.code === 'string' ? data.code : undefined };
    }

    if (typeof data === 'object' && data !== null) {
      const fieldErrors: Record<string, string[]> = {};
      let hasFieldErrors = false;

      for (const [key, value] of Object.entries(data)) {
        if (Array.isArray(value)) {
          fieldErrors[key] = value.map(String);
          hasFieldErrors = true;
        }
      }

      if (hasFieldErrors) {
        return {
          message: 'Validation error',
          status,
          code: typeof data.code === 'string' ? data.code : undefined,
          errors: fieldErrors,
        };
      }
    }

    return {
      message: axiosError.message || 'An unexpected error occurred',
      status,
      code: typeof data?.code === 'string' ? data.code : undefined,
    };
  }

  return {
    message: 'An unexpected error occurred',
    status: 500,
  };
};

export const login = async (
  username: string,
  password: string
): Promise<void> => {
  // Backend sets httpOnly cookies; the response interceptor captures the CSRF token.
  await apiClient.post('/api/v1/auth/login/', { username, password });
  setAuthFlag();
};

export const logout = async (): Promise<void> => {
  try {
    await apiClient.post('/api/v1/auth/logout/');
  } catch {
    // Best-effort — clear client-side flag even if request fails
  }
  clearTokens();
  window.location.href = '/login';
};

export default apiClient;
